import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  sanaNotes: [],
  currentSanaNote: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Sana Notes (optionally by patient ID)
export const fetchSanaNotes = createAsyncThunk(
  'sanaNotes/fetchSanaNotes',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/sana-notes/patient/${patientId}`
        : '/sana-notes';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Sana notes'
      );
    }
  }
);

// Fetch a single Sana Note by ID
export const fetchSanaNote = createAsyncThunk(
  'sanaNotes/fetchSanaNote',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/sana-notes/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Sana note'
      );
    }
  }
);

// Create a new Sana Note
export const createSanaNote = createAsyncThunk(
  'sanaNotes/createSanaNote',
  async (sanaNoteData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/sana-notes`, sanaNoteData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Sana note'
      );
    }
  }
);

// Update a Sana Note
export const updateSanaNote = createAsyncThunk(
  'sanaNotes/updateSanaNote',
  async ({ case_id, sanaNoteData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/sana-notes/case/${case_id}`, sanaNoteData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Sana note'
      );
    }
  }
);

// Delete a Sana Note
export const deleteSanaNote = createAsyncThunk(
  'sanaNotes/deleteSanaNote',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/sana-notes/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Sana note'
      );
    }
  }
);

// ==================== Slice ====================
const sanaNotesSlice = createSlice({
  name: 'sanaNotes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentSanaNote: (state, action) => {
      state.currentSanaNote = action.payload;
    },
    clearCurrentSanaNote: (state) => {
      state.currentSanaNote = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchSanaNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSanaNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sanaNotes = action.payload;
      })
      .addCase(fetchSanaNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchSanaNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSanaNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSanaNote = action.payload;
      })
      .addCase(fetchSanaNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createSanaNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSanaNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sanaNotes.unshift(action.payload);
      })
      .addCase(createSanaNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateSanaNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSanaNote.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.sanaNotes.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.sanaNotes[index] = updated;
        }

        if (state.currentSanaNote?.id === updated.id) {
          state.currentSanaNote = updated;
        }
      })
      .addCase(updateSanaNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteSanaNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSanaNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sanaNotes = state.sanaNotes.filter((d) => d.id !== action.payload);

        if (state.currentSanaNote?.id === action.payload) {
          state.currentSanaNote = null;
        }
      })
      .addCase(deleteSanaNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentSanaNote, clearCurrentSanaNote } = sanaNotesSlice.actions;
export default sanaNotesSlice.reducer;
